import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Typography,
} from '@material-ui/core';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';

import { RootState } from '../../store';
import {
  fetchBOMByIdStart,
  createBOMStart,
  updateBOMStart,
  closeBOMDialog,
  clearBOMError,
  clearBOMValidationErrors,
  fetchDropdownsStart,
} from '../../store/reducers/bomSlice';
import { BOM, CreateBOMRequest } from '../../services/bomApi';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  title: {
    color: theme.palette.primary.main,
    fontWeight: 'bold',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  formRow: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'flex-start',
  },
  formField: {
    flex: 1,
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
  errorList: {
    color: theme.palette.error.main,
    listStyle: 'none',
    padding: 0,
    margin: theme.spacing(1, 0),
  },
  errorItem: {
    marginBottom: theme.spacing(0.5),
  },
  dialogContent: {
    minWidth: 500,
  },
}));

const BOMEdit: React.FC = () => {
  const classes = useStyles();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isEditMode = Boolean(id);

  const {
    selectedBOM,
    bomLoading,
    bomError,
    bomValidationErrors,
    bomDialogOpen,
    editingMode,
    dropdownComponents,
    dropdownProducts,
    dropdownsLoading,
  } = useSelector((state: RootState) => state.bom);

  const [formData, setFormData] = useState<Partial<BOM>>({
    Name: '',
    ProductId: '',
    ComponentId: '',
    Document: '',
    Active: true,
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchDropdownsStart());
    
    if (isEditMode && id) {
      dispatch(fetchBOMByIdStart(id));
    }
  }, [dispatch, id, isEditMode]);

  useEffect(() => {
    if (selectedBOM && isEditMode) {
      setFormData({
        id: selectedBOM.id,
        Name: selectedBOM.Name || '',
        ProductId: selectedBOM.ProductId || '',
        ComponentId: selectedBOM.ComponentId || '',
        Document: selectedBOM.Document || '',
        Active: selectedBOM.Active !== undefined ? selectedBOM.Active : true,
      });
    }
  }, [selectedBOM, isEditMode]);

  useEffect(() => {
    if (bomError) {
      setSnackbarOpen(true);
    }
  }, [bomError]);

  const handleInputChange = (field: keyof BOM) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSelectChange = (field: keyof BOM) => (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value as string,
    }));
  };

  const handleCheckboxChange = (field: keyof BOM) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.checked,
    }));
  };

  const handleSave = () => {
    dispatch(clearBOMValidationErrors());

    const bomData: CreateBOMRequest = {
      Name: formData.Name || '',
      ProductId: formData.ProductId || '',
      ComponentId: formData.ComponentId || '',
      Document: formData.Document || '',
      Active: formData.Active !== undefined ? formData.Active : true,
    };

    if (isEditMode && formData.id) {
      dispatch(updateBOMStart({
        ...bomData,
        id: formData.id,
      }));
    } else {
      dispatch(createBOMStart(bomData));
    }
  };

  const handleCancel = () => {
    if (bomDialogOpen) {
      dispatch(closeBOMDialog());
    } else {
      navigate('/bom');
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    dispatch(clearBOMError());
  };

  if (dropdownsLoading) {
    return (
      <Box className={classes.root}>
        <div className={classes.loadingContainer}>
          <CircularProgress size={50} />
          <Typography variant="body1" style={{ marginLeft: 16 }}>
            Loading dropdown data...
          </Typography>
        </div>
      </Box>
    );
  }

  const BOMForm = () => (
    <div className={classes.form}>
      <div className={classes.formRow}>
        <TextField
          label="BOM Name"
          variant="outlined"
          fullWidth
          required
          value={formData.Name || ''}
          onChange={handleInputChange('Name')}
          error={bomValidationErrors.some(error => error.includes('BOM Name'))}
          helperText="Enter a descriptive name for this bill of materials"
          className={classes.formField}
        />
      </div>

      <div className={classes.formRow}>
        <FormControl
          variant="outlined"
          fullWidth
          required
          className={classes.formField}
          error={bomValidationErrors.some(error => error.includes('Product'))}
        >
          <InputLabel>Product</InputLabel>
          <Select
            value={formData.ProductId || ''}
            onChange={handleSelectChange('ProductId')}
            label="Product"
          >
            <MenuItem value="">
              <em>Select a Product</em>
            </MenuItem>
            {dropdownProducts.map((product) => (
              <MenuItem key={product.id} value={product.id}>
                {product.Name} {product.ProductNumber && `(${product.ProductNumber})`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          variant="outlined"
          fullWidth
          required
          className={classes.formField}
          error={bomValidationErrors.some(error => error.includes('Component'))}
        >
          <InputLabel>Component</InputLabel>
          <Select
            value={formData.ComponentId || ''}
            onChange={handleSelectChange('ComponentId')}
            label="Component"
          >
            <MenuItem value="">
              <em>Select a Component</em>
            </MenuItem>
            {dropdownComponents.map((component) => (
              <MenuItem key={component.id} value={component.id}>
                {component.Name} {component.PartNumber && `(${component.PartNumber})`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <div className={classes.formRow}>
        <TextField
          label="Document"
          variant="outlined"
          fullWidth
          multiline
          rows={3}
          value={formData.Document || ''}
          onChange={handleInputChange('Document')}
          helperText="Optional documentation or notes for this BOM"
          className={classes.formField}
        />
      </div>

      <div className={classes.formRow}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.Active || false}
              onChange={handleCheckboxChange('Active')}
              color="primary"
            />
          }
          label="Active"
        />
      </div>

      {bomValidationErrors.length > 0 && (
        <Box>
          <Typography variant="body2" color="error" gutterBottom>
            Please fix the following errors:
          </Typography>
          <ul className={classes.errorList}>
            {bomValidationErrors.map((error, index) => (
              <li key={index} className={classes.errorItem}>
                • {error}
              </li>
            ))}
          </ul>
        </Box>
      )}

      <div className={classes.actions}>
        <Button
          variant="outlined"
          startIcon={<CancelIcon />}
          onClick={handleCancel}
          disabled={bomLoading}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={bomLoading || !formData.Name || !formData.ProductId || !formData.ComponentId}
        >
          {bomLoading ? <CircularProgress size={20} /> : (isEditMode ? 'Update BOM' : 'Create BOM')}
        </Button>
      </div>
    </div>
  );

  // If used as a dialog (from BOM list)
  if (bomDialogOpen) {
    return (
      <Dialog
        open={bomDialogOpen}
        onClose={handleCancel}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingMode === 'create' ? 'Create New BOM' : 'Edit BOM'}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <BOMForm />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} disabled={bomLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={bomLoading || !formData.Name || !formData.ProductId || !formData.ComponentId}
          >
            {bomLoading ? <CircularProgress size={20} /> : (editingMode === 'edit' ? 'Update' : 'Create')}
          </Button>
        </DialogActions>

        {/* Error Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          message={bomError}
        />
      </Dialog>
    );
  }

  // Standalone page view
  return (
    <Box className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h4" className={classes.title}>
          {isEditMode ? 'Edit BOM' : 'Create New BOM'}
        </Typography>
      </div>

      <Paper className={classes.paper}>
        <BOMForm />
      </Paper>

      {/* Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        message={bomError}
      />
    </Box>
  );
};

export default BOMEdit; 